// @system — Minimal markdown renderer for posts without server-rendered `content_html`
// (the bundled seed posts, or a DB row written before the renderer existed). Handles the subset
// the seed data uses: ## / ###, fenced code, - and 1. lists, **bold**, `code`, [links](url).
import { ScrollRail } from '@/app/components/@system/ScrollRail'

/** @param {{ text: string }} props */
function InlineContent({ text }) {
  /** @type {import('react').ReactNode[]} */
  const tokens = []
  const pattern = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g
  let last = 0
  let match

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) tokens.push(text.slice(last, match.index))

    if (match[1].startsWith('**')) {
      tokens.push(<strong key={match.index}>{match[2]}</strong>)
    } else if (match[1].startsWith('`')) {
      tokens.push(
        <code key={match.index} className="rounded bg-brand-surface px-1 py-0.5 font-mono text-xs">
          {match[3]}
        </code>,
      )
    } else {
      tokens.push(
        <a key={match.index} href={match[5]} className="min-h-6 text-brand-primary underline underline-offset-4 hover:opacity-80">
          {match[4]}
        </a>,
      )
    }

    last = match.index + match[0].length
  }

  if (last < text.length) tokens.push(text.slice(last))

  return <>{tokens}</>
}

/** @param {{ raw: string }} props */
export function MarkdownContent({ raw }) {
  const blocks = raw.split(/\n\n+/)

  return (
    <div className="prose prose-sm sm:prose max-w-none dark:prose-invert" data-testid="markdown-content">
      {blocks.map((block, i) => {
        // Fenced code block
        if (block.startsWith('```')) {
          const lines = block.split('\n')
          const lang = lines[0].replace('```', '').trim()
          const code = lines.slice(1, lines[lines.length - 1] === '```' ? -1 : undefined).join('\n')
          return (
            <ScrollRail as="pre" key={i} className="rounded-lg bg-brand-surface p-4 text-sm font-mono border my-4">
              {lang && <span className="block text-xs text-brand-text-muted mb-2">{lang}</span>}
              <code>{code}</code>
            </ScrollRail>
          )
        }

        // H2
        if (block.startsWith('## ')) {
          return (
            <h2 key={i} className="text-xl font-bold mt-8 mb-3">
              {block.replace('## ', '')}
            </h2>
          )
        }

        // H3
        if (block.startsWith('### ')) {
          return (
            <h3 key={i} className="text-base font-semibold mt-6 mb-2">
              {block.replace('### ', '')}
            </h3>
          )
        }

        // Unordered list
        if (block.includes('\n-') || block.startsWith('- ')) {
          const items = block.split('\n').filter((l) => l.startsWith('- '))
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 my-3">
              {items.map((item, j) => (
                <li key={j} className="text-sm text-brand-text">
                  <InlineContent text={item.replace(/^- /, '')} />
                </li>
              ))}
            </ul>
          )
        }

        // Numbered list
        if (/^\d+\./.test(block)) {
          const items = block.split('\n').filter((l) => /^\d+\./.test(l))
          return (
            <ol key={i} className="list-decimal pl-5 space-y-1 my-3">
              {items.map((item, j) => (
                <li key={j} className="text-sm text-brand-text">
                  <InlineContent text={item.replace(/^\d+\.\s*/, '')} />
                </li>
              ))}
            </ol>
          )
        }

        // Regular paragraph
        return (
          <p key={i} className="text-sm text-brand-text leading-relaxed my-3">
            <InlineContent text={block} />
          </p>
        )
      })}
    </div>
  )
}
